import React, { useEffect, useMemo, useState } from "react";
import { Search, X, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { fetchDictionary } from "@/lib/api";

const Letter = ({ letter, terms, onPick, active }) => (
  <button
    onClick={() => onPick(letter)}
    data-testid={`dict-letter-${letter}`}
    disabled={!terms}
    className={`w-9 h-9 flex items-center justify-center font-body text-sm border transition-colors ${
      active ? "bg-emerald text-cream border-emerald" : terms ? "border-line hover:border-emerald hover:text-emerald" : "border-line/40 text-ink/25"
    }`}
  >
    {letter}
  </button>
);

const Dictionary = () => {
  const [data, setData] = useState({ terms: [] });
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { fetchDictionary().then(setData).catch(() => setData({ terms: [] })); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (data.terms || []).filter((t) => {
      if (letter && !t.term.toLowerCase().startsWith(letter.toLowerCase())) return false;
      if (query && !`${t.term} ${t.definition}`.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [data, q, letter]);

  const availableLetters = useMemo(() => {
    const s = new Set((data.terms || []).map((t) => t.term[0].toUpperCase()));
    return s;
  }, [data]);

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  return (
    <main className="min-h-screen bg-cream pt-32 pb-24" data-testid="dictionary-page">
      <section className="bg-emerald text-cream py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow text-gold mb-4 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Love Genome Dictionary
            </p>
            <h1 className="font-display text-5xl md:text-6xl leading-[0.95]">
              The words behind <span className="italic text-gold">the framework.</span>
            </h1>
            <p className="font-editorial italic text-2xl text-cream/75 mt-5 max-w-2xl">
              A working vocabulary of {data.terms?.length || 0} terms — the language you need to name what love is asking of you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pt-12">
        <div className="flex flex-col md:flex-row gap-4 md:items-center mb-8">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-ink/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search terms, definitions, virtues…"
              data-testid="dict-search"
              className="w-full pl-10 pr-10 py-3 bg-surface border border-line focus:border-emerald outline-none font-body"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink" aria-label="Clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="text-xs font-body text-ink/50 shrink-0">
            {filtered.length} of {data.terms?.length || 0} shown
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-10" data-testid="dict-alphabet">
          <button
            onClick={() => setLetter(null)}
            className={`h-9 px-3 font-body text-xs uppercase tracking-widest border transition-colors ${
              !letter ? "bg-emerald text-cream border-emerald" : "border-line hover:border-emerald hover:text-emerald"
            }`}
            data-testid="dict-letter-all"
          >
            All
          </button>
          {alphabet.map((l) => (
            <Letter key={l} letter={l} terms={availableLetters.has(l)} onPick={setLetter} active={letter === l} />
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-center font-editorial italic text-2xl text-ink/40 py-16" data-testid="dict-empty">
            No terms match your search.
          </p>
        ) : (
          <ul className="space-y-4">
            {filtered.map((t) => {
              const open = expanded === t.term;
              return (
                <li key={t.term} className="border border-line bg-surface" data-testid={`dict-term-${t.term.replace(/\s+/g, "-").toLowerCase()}`}>
                  <button
                    onClick={() => setExpanded(open ? null : t.term)}
                    className="w-full text-left p-6 md:p-8 flex flex-col md:flex-row md:items-baseline md:gap-8"
                  >
                    <div className="md:w-64 shrink-0">
                      <p className="eyebrow text-emerald mb-1">{t.pillar}</p>
                      <h3 className="font-display text-2xl md:text-3xl text-ink">{t.term}</h3>
                    </div>
                    <div className="flex-1">
                      <p className="font-body text-ink/80 leading-relaxed">{t.definition}</p>
                      {open && (
                        <div className="mt-5 pt-5 border-t border-line/60 space-y-3">
                          {t.example && (
                            <p className="font-editorial italic text-emerald text-lg">&ldquo;{t.example.replace(/^"/, "").replace(/"$/, "")}&rdquo;</p>
                          )}
                          {t.related?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              <span className="text-[10px] uppercase tracking-widest text-ink/45 font-body">Related</span>
                              {t.related.map((r) => (
                                <span key={r} className="text-[11px] font-body text-emerald border border-emerald/30 px-2 py-0.5">{r}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-16 flex flex-wrap gap-3">
          <Link to="/read/game" className="btn-emerald" data-testid="dict-to-game">
            Play the Matching Game
          </Link>
          <Link to="/read" className="btn-outline" data-testid="dict-to-read">
            Back to the Reader
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Dictionary;
